// src/stores/modelStore.tsx
/**
 * Model Store - Enhanced with Color Support
 *
 * Changes from original:
 * 1. Added color support to ModelItem type (via schema update)
 * 2. Added updateItemColor action
 * 3. Added updateMultipleColors action for batch updates
 * 4. Added resetItemColor action
 * 5. Added helper methods for color management
 * 6. Maintains backward compatibility with existing code
 */

import React, { createContext, useRef, useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { produce } from 'immer';
import { ModelStore, ModelItem } from 'src/types';
import { INITIAL_DATA } from 'src/config';
import { getItemByIdOrThrow } from 'src/utils';

const initialState = () => {
  return createStore<ModelStore>((set, get) => {
    type Snapshot = Omit<ModelStore, 'actions' | 'historyVersion'>;
    const undoStack: Snapshot[] = [];
    const redoStack: Snapshot[] = [];
    let lastMutationAt = 0;
    let isRestoringHistory = false;

    const snapshot = (state: ModelStore): Snapshot => {
      const { actions: _actions, historyVersion: _historyVersion, ...model } = state;
      void _actions;
      void _historyVersion;
      return structuredClone(model) as Snapshot;
    };

    const trackedSet: typeof set = (partial, replace) => {
      if (!isRestoringHistory) {
        const now = Date.now();
        if (now - lastMutationAt > 250) {
          undoStack.push(snapshot(get()));
          if (undoStack.length > 100) undoStack.shift();
        }
        lastMutationAt = now;
        redoStack.length = 0;
      }
      set(partial as never, replace as never);
      set({ historyVersion: get().historyVersion + 1 });
    };

    const restore = (next: Snapshot) => {
      isRestoringHistory = true;
      set({ ...next, historyVersion: get().historyVersion + 1 });
      isRestoringHistory = false;
      lastMutationAt = 0;
    };

    return {
      ...INITIAL_DATA,
      historyVersion: 0,
      actions: {
        get,
        set: trackedSet,
        undo: () => {
          const previous = undoStack.pop();
          if (!previous) return;
          redoStack.push(snapshot(get()));
          restore(previous);
        },
        redo: () => {
          const next = redoStack.pop();
          if (!next) return;
          undoStack.push(snapshot(get()));
          restore(next);
        },
        canUndo: () => undoStack.length > 0,
        canRedo: () => redoStack.length > 0,
        clearHistory: () => {
          undoStack.length = 0;
          redoStack.length = 0;
          lastMutationAt = 0;
          set({ historyVersion: get().historyVersion + 1 });
        },

        /**
         * Update the diagram title
         * @param title - The new title for the diagram
         */
        setTitle: (title: string) => {
          trackedSet({ title: title.trim() || 'Untitled Diagram' });
        },

        /**
         * Update the diagram description
         * @param description - The new description for the diagram
         */
        setDescription: (description: string) => {
          trackedSet({ description });
        },

        /**
         * Update the color of a single item
         * @param itemId - The ID of the item to update
         * @param color - The new color (hex format, e.g., '#3b82f6')
         */
        updateItemColor: (itemId: string, color: string) => {
          trackedSet((state) =>
            produce(state, (draft) => {
              const { index } = getItemByIdOrThrow(draft.items, itemId);
              draft.items[index].color = color;
            })
          );
        },

        /**
         * Batch update colors for multiple items
         * @param updates - Array of {id, color} objects
         */
        updateMultipleColors: (
          updates: Array<{ id: string; color: string }>
        ) => {
          trackedSet((state) =>
            produce(state, (draft) => {
              updates.forEach(({ id, color }) => {
                try {
                  const { index } = getItemByIdOrThrow(draft.items, id);
                  draft.items[index].color = color;
                } catch (error) {
                  console.warn(`Failed to update color for item ${id}:`, error);
                }
              });
            })
          );
        },

        /**
         * Reset item color to undefined (will use default)
         * @param itemId - The ID of the item to reset
         */
        resetItemColor: (itemId: string) => {
          trackedSet((state) =>
            produce(state, (draft) => {
              const { index } = getItemByIdOrThrow(draft.items, itemId);
              delete draft.items[index].color;
            })
          );
        },

        /**
         * Get the color of an item (returns default if not set)
         * @param itemId - The ID of the item
         * @returns The color hex string or default color
         */
        getItemColor: (itemId: string): string => {
          const state = get();
          const item = state.items.find((i) => i.id === itemId);
          return item?.color || '#3b82f6'; // Default blue
        },

        /**
         * Get all items with a specific color
         * @param color - The color to filter by
         * @returns Array of items with that color
         */
        getItemsByColor: (color: string): ModelItem[] => {
          const state = get();
          return state.items.filter((item) => item.color === color);
        },

        /**
         * Check if an item has a custom color
         * @param itemId - The ID of the item
         * @returns true if item has a custom color set
         */
        hasCustomColor: (itemId: string): boolean => {
          const state = get();
          const item = state.items.find((i) => i.id === itemId);
          return item?.color !== undefined;
        },

        /**
         * Get all unique colors used in the diagram
         * @returns Array of unique color hex strings
         */
        getUsedColors: (): string[] => {
          const state = get();
          const colors = new Set<string>();
          state.items.forEach((item) => {
            if (item.color) {
              colors.add(item.color);
            }
          });
          return Array.from(colors);
        },

        /**
         * Apply a color to all items of a specific type
         * @param iconId - The icon ID to match
         * @param color - The color to apply
         */
        colorItemsByType: (iconId: string, color: string) => {
          trackedSet((state) =>
            produce(state, (draft) => {
              draft.items.forEach((item) => {
                if (item.icon === iconId) {
                  item.color = color;
                }
              });
            })
          );
        }
      }
    };
  });
};

export const ModelContext = createContext<ReturnType<typeof initialState> | null>(
  null
);

interface ProviderProps {
  children: React.ReactNode;
}

/**
 * Model Provider - Provides model store to component tree
 */
export const ModelProvider = ({ children }: ProviderProps) => {
  const storeRef = useRef<ReturnType<typeof initialState>>();

  if (!storeRef.current) {
    storeRef.current = initialState();
  }

  return (
    <ModelContext.Provider value={storeRef.current}>
      {children}
    </ModelContext.Provider>
  );
};

/**
 * Hook to access model store
 */
export function useModelStore<T>(
  selector: (state: ModelStore) => T,
  equalityFn?: (left: T, right: T) => boolean
) {
  const store = useContext(ModelContext);

  if (store === null) {
    throw new Error('Missing ModelProvider in the tree');
  }

  const value = useStore(store, selector, equalityFn);

  return value;
}

// ===== CONVENIENCE HOOKS =====

/**
 * Hook to get an item's color
 * @param itemId - The ID of the item
 * @returns The item's color or default color
 */
export const useItemColor = (itemId: string): string => {
  return useModelStore((state) => {
    const item = state.items.find((i) => i.id === itemId);
    return item?.color || '#3b82f6';
  });
};

/**
 * Hook to check if an item has a custom color
 * @param itemId - The ID of the item
 * @returns true if the item has a custom color set
 */
export const useHasCustomColor = (itemId: string): boolean => {
  return useModelStore((state) => {
    const item = state.items.find((i) => i.id === itemId);
    return item?.color !== undefined;
  });
};

/**
 * Hook to get all items with a specific color
 * @param color - The color to filter by
 * @returns Array of items with that color
 */
export const useItemsByColor = (color: string): ModelItem[] => {
  return useModelStore((state) =>
    state.items.filter((item) => item.color === color)
  );
};

/**
 * Hook to get all unique colors used in the diagram
 * @returns Array of unique color hex strings
 */
export const useUsedColors = (): string[] => {
  return useModelStore((state) => {
    const colors = new Set<string>();
    state.items.forEach((item) => {
      if (item.color) {
        colors.add(item.color);
      }
    });
    return Array.from(colors);
  });
};

/**
 * Hook to get color update actions
 * @returns Object with color management functions
 */
export const useColorActions = () => {
  return useModelStore((state) => ({
    updateItemColor: state.actions.updateItemColor,
    updateMultipleColors: state.actions.updateMultipleColors,
    resetItemColor: state.actions.resetItemColor,
    getItemColor: state.actions.getItemColor,
    getItemsByColor: state.actions.getItemsByColor,
    hasCustomColor: state.actions.hasCustomColor,
    getUsedColors: state.actions.getUsedColors,
    colorItemsByType: state.actions.colorItemsByType
  }));
};
